export type Coords = { lat: number; lon: number };
export type BikeModel = { id: number; name: string };
export type Station = {
  lat: number;
  lon: number;
  address: string;
  isOpen: boolean;
  bikeModels: BikeModel[];
};
export type StationWithDistance = Station & { distanceKm: number };

type DistanceLocale = 'pt-BR' | 'en';

const EARTH_RADIUS_KM = 6371.0088;

/* Five decimals is about one metre, which is finer than the device itself reports. It
   exists to stop a float such as -23.550412345678901 from leaving the page as seventeen
   digits of false precision. */
export const COORD_DECIMALS = 5;

export const roundTo = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

export const haversineKm = (a: Coords, b: Coords): number => {
  const halfDeltaLat = toRadians(b.lat - a.lat) / 2;
  const halfDeltaLon = toRadians(b.lon - a.lon) / 2;
  const h =
    Math.sin(halfDeltaLat) ** 2 +
    Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(halfDeltaLon) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(h));
};

export const rankStations = (
  from: Coords,
  stations: readonly Station[],
): StationWithDistance[] =>
  stations
    .map((station) => ({ ...station, distanceKm: haversineKm(from, station) }))
    .sort((a, b) => a.distanceKm - b.distanceKm);

export const countWithin = (ranked: readonly StationWithDistance[], km: number): number =>
  ranked.filter((station) => station.distanceKm <= km).length;

// Intl formatting is avoided: a Node build without full ICU silently falls back
// to the English separator and the pt-BR copy would read '3.5 km'.
const decimalSeparator = (locale: DistanceLocale): string => (locale === 'en' ? '.' : ',');

const formatHalfKm = (km: number, locale: DistanceLocale): string => {
  const rounded = Math.round(km * 2) / 2;
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return `${Math.trunc(rounded)}${decimalSeparator(locale)}5`;
};

export const formatDistanceKm = (km: number, locale: DistanceLocale): string => {
  if (km < 1) {
    return locale === 'en' ? 'under 1 km' : 'menos de 1 km';
  }
  if (km < 10) {
    return `≈ ${formatHalfKm(km, locale)} km`;
  }
  return `≈ ${Math.round(km)} km`;
};
